import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import DataTable from "../components/DataTable";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Pencil, ShieldCheck } from "lucide-react";

const ROLE_OPTIONS = [
  "super_admin", "group_hr_head", "entity_hr_head", "hiring_manager", "recruiter",
  "interview_panelist", "candidate", "ai_knowledge_admin", "finance_approver",
  "business_head", "onboarding_specialist", "auditor", "integration_admin", "employee_referrer",
];

const SIMPLE_TABS = [
  { key: "entities", label: "Entities",
    fields: [["name", "Name"], ["code", "Code"], ["industry", "Industry"], ["description", "Description"]] },
  { key: "departments", label: "Departments",
    fields: [["name", "Name"], ["entity_id", "Entity", "entity"], ["head_name", "Head"]] },
  { key: "locations", label: "Locations",
    fields: [["name", "Name"], ["city", "City"], ["country", "Country"]] },
  { key: "salary-bands", label: "Salary Bands",
    fields: [["band", "Band"], ["level", "Level"], ["min_lpa", "Min LPA", "number"], ["max_lpa", "Max LPA", "number"]] },
  { key: "stages", label: "Pipeline Stages",
    fields: [["key", "Key"], ["label", "Label"], ["order", "Order", "number"], ["color", "Color"]] },
  { key: "connectors", label: "Sourcing Connectors",
    fields: [["name", "Name (id)"], ["label", "Label"], ["mode", "Mode"], ["order", "Order", "number"], ["description", "Description"]] },
];

const COMPLEX_TABS = [
  { key: "workflows", label: "Approval Workflows" },
  { key: "interview-templates", label: "Interview Templates" },
  { key: "onboarding-templates", label: "Onboarding Templates" },
  { key: "settings", label: "Platform Settings" },
  { key: "users", label: "Users & Roles" },
];

const TABS = [...SIMPLE_TABS, ...COMPLEX_TABS];

function useEntities() {
  const [entities, setEntities] = useState([]);
  useEffect(() => { api.get("/master/entities").then((r) => setEntities(r.data)).catch(() => {}); }, []);
  return entities;
}

/* ---------------- Approval Workflow builder ---------------- */
function WorkflowsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", steps: [{ order: 1, approver_role: "entity_hr_head", label: "HR Review" }] });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/master/workflows").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.steps.length) return toast.error("Name and at least one step required");
    try {
      if (editing) await api.put(`/master/workflows/${editing}`, form);
      else await api.post("/master/workflows", form);
      toast.success(editing ? "Workflow updated" : "Workflow created");
      setForm({ name: "", steps: [{ order: 1, approver_role: "entity_hr_head", label: "HR Review" }] });
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (w) => { setForm({ name: w.name, steps: w.steps }); setEditing(w.id); };
  const remove = async (id) => { await api.delete(`/master/workflows/${id}`); load(); toast.success("Deleted"); };

  const setStep = (i, key, v) => {
    const s = [...form.steps]; s[i] = { ...s[i], [key]: key === "order" ? +v : v }; setForm({ ...form, steps: s });
  };
  const addStep = () => setForm({ ...form, steps: [...form.steps, { order: form.steps.length + 1, approver_role: "finance_approver", label: "New Step" }] });
  const rmStep = (i) => setForm({ ...form, steps: form.steps.filter((_, x) => x !== i) });

  return (
    <div className="border border-t-0 bg-white p-5" data-testid="workflows-panel">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">{editing ? "Edit workflow" : "New workflow"}</div>
          <input data-testid="wf-name-input" placeholder="Workflow name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border px-3 py-2 text-sm mb-3" />
          <div className="space-y-2">
            {form.steps.map((s, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`wf-step-${i}`}>
                <input type="number" value={s.order} onChange={(e) => setStep(i, "order", e.target.value)} className="border px-2 py-1.5 text-sm w-14" />
                <select value={s.approver_role} onChange={(e) => setStep(i, "approver_role", e.target.value)} className="border px-2 py-1.5 text-sm bg-white flex-1">
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                </select>
                <input value={s.label} onChange={(e) => setStep(i, "label", e.target.value)} className="border px-2 py-1.5 text-sm flex-1" />
                <button onClick={() => rmStep(i)} className="text-muted-foreground hover:text-red-600 p-1"><X size={14} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={addStep} className="text-xs font-bold border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150">+ Add step</button>
            <button data-testid="wf-save-button" onClick={save} className="ml-auto bg-[#0A251D] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
              {editing ? "Update" : "Create"}
            </button>
            {editing && <button onClick={() => { setEditing(null); setForm({ name: "", steps: [{ order: 1, approver_role: "entity_hr_head", label: "HR Review" }] }); }} className="border px-3 py-1.5 text-xs">Cancel</button>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Configured workflows ({rows.length})</div>
          <div className="space-y-3">
            {rows.map((w) => (
              <div key={w.id} className="border p-4" data-testid={`wf-row-${w.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{w.name}</div>
                  <div className="flex gap-2">
                    <button onClick={() => edit(w)} className="text-muted-foreground hover:text-[#0A251D]"><Pencil size={13} /></button>
                    <button onClick={() => remove(w.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {w.steps?.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="border px-3 py-2 text-xs">
                        <div className="font-semibold">{s.order}. {s.label}</div>
                        <div className="text-muted-foreground capitalize">{s.approver_role.replace(/_/g, " ")}</div>
                      </div>
                      {i < w.steps.length - 1 && <span className="text-muted-foreground">→</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Interview Templates ---------------- */
function InterviewTemplatesPanel() {
  const entities = useEntities();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", entity_id: "", is_default: false, rounds: [{ order: 1, name: "Screening Call", type: "screening", duration_mins: 30 }] });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/master/interview-templates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.rounds.length) return toast.error("Name and at least one round required");
    try {
      const payload = { ...form, entity_id: form.entity_id || null };
      if (editing) await api.put(`/master/interview-templates/${editing}`, payload);
      else await api.post("/master/interview-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setForm({ name: "", entity_id: "", is_default: false, rounds: [{ order: 1, name: "Screening Call", type: "screening", duration_mins: 30 }] });
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (t) => { setForm({ name: t.name, entity_id: t.entity_id || "", is_default: t.is_default, rounds: t.rounds }); setEditing(t.id); };
  const remove = async (id) => { await api.delete(`/master/interview-templates/${id}`); load(); toast.success("Deleted"); };
  const setRound = (i, k, v) => { const r = [...form.rounds]; r[i] = { ...r[i], [k]: k === "order" || k === "duration_mins" ? +v : v }; setForm({ ...form, rounds: r }); };
  const addRound = () => setForm({ ...form, rounds: [...form.rounds, { order: form.rounds.length + 1, name: "New Round", type: "technical", duration_mins: 45 }] });
  const rmRound = (i) => setForm({ ...form, rounds: form.rounds.filter((_, x) => x !== i) });

  return (
    <div className="border border-t-0 bg-white p-5 grid md:grid-cols-2 gap-6" data-testid="iv-templates-panel">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">{editing ? "Edit template" : "New interview template"}</div>
        <input data-testid="iv-tpl-name" placeholder="Template name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border px-3 py-2 text-sm mb-2" />
        <div className="flex items-center gap-3 mb-3">
          <select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="border px-3 py-2 text-sm bg-white flex-1">
            <option value="">Global (all entities)</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-[#0A251D]" /> Default</label>
        </div>
        <div className="space-y-2">
          {form.rounds.map((r, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input type="number" value={r.order} onChange={(e) => setRound(i, "order", e.target.value)} className="border px-2 py-1.5 text-sm w-14" />
              <input value={r.name} onChange={(e) => setRound(i, "name", e.target.value)} className="border px-2 py-1.5 text-sm flex-1" />
              <select value={r.type} onChange={(e) => setRound(i, "type", e.target.value)} className="border px-2 py-1.5 text-sm bg-white">
                {["screening", "technical", "managerial", "hr", "culture"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <input type="number" value={r.duration_mins} onChange={(e) => setRound(i, "duration_mins", e.target.value)} className="border px-2 py-1.5 text-sm w-20" />
              <button onClick={() => rmRound(i)} className="text-muted-foreground hover:text-red-600 p-1"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={addRound} className="text-xs font-bold border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150">+ Add round</button>
          <button data-testid="iv-tpl-save" onClick={save} className="ml-auto bg-[#0A251D] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">{editing ? "Update" : "Create"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm({ name: "", entity_id: "", is_default: false, rounds: [{ order: 1, name: "Screening Call", type: "screening", duration_mins: 30 }] }); }} className="border px-3 py-1.5 text-xs">Cancel</button>}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Configured templates ({rows.length})</div>
        <div className="space-y-3">
          {rows.map((t) => (
            <div key={t.id} className="border p-4" data-testid={`iv-tpl-row-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-sm">{t.name}</span>
                  {t.is_default && <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#0A251D] text-white px-2 py-0.5 rounded font-bold">Default</span>}
                  <span className="ml-2 text-xs text-muted-foreground">{entities.find((e) => e.id === t.entity_id)?.name || "Global"}</span>
                </div>
                <div className="flex gap-2"><button onClick={() => edit(t)} className="text-muted-foreground hover:text-[#0A251D]"><Pencil size={13} /></button><button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={13} /></button></div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {t.rounds?.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="border px-2 py-1 text-xs"><div className="font-semibold">{r.order}. {r.name}</div><div className="text-muted-foreground">{r.type} · {r.duration_mins}m</div></div>
                    {i < t.rounds.length - 1 && <span className="text-muted-foreground">→</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Onboarding Templates ---------------- */
function OnboardingTemplatesPanel() {
  const entities = useEntities();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", entity_id: "", is_default: false, tasks: ["Sign offer letter"] });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/master/onboarding-templates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || form.tasks.length === 0) return toast.error("Name and tasks required");
    try {
      const payload = { ...form, entity_id: form.entity_id || null, tasks: form.tasks.filter((t) => t.trim()) };
      if (editing) await api.put(`/master/onboarding-templates/${editing}`, payload);
      else await api.post("/master/onboarding-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setForm({ name: "", entity_id: "", is_default: false, tasks: [""] });
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (t) => { setForm({ name: t.name, entity_id: t.entity_id || "", is_default: t.is_default, tasks: t.tasks }); setEditing(t.id); };
  const remove = async (id) => { await api.delete(`/master/onboarding-templates/${id}`); load(); toast.success("Deleted"); };

  return (
    <div className="border border-t-0 bg-white p-5 grid md:grid-cols-2 gap-6" data-testid="onb-templates-panel">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">{editing ? "Edit template" : "New onboarding template"}</div>
        <input data-testid="onb-tpl-name" placeholder="Template name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border px-3 py-2 text-sm mb-2" />
        <div className="flex items-center gap-3 mb-3">
          <select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="border px-3 py-2 text-sm bg-white flex-1">
            <option value="">Global (all entities)</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-[#0A251D]" /> Default</label>
        </div>
        <div className="space-y-2">
          {form.tasks.map((t, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs text-muted-foreground w-6 py-2">{i + 1}.</span>
              <input value={t} onChange={(e) => { const x = [...form.tasks]; x[i] = e.target.value; setForm({ ...form, tasks: x }); }} className="border px-2 py-1.5 text-sm flex-1" />
              <button onClick={() => setForm({ ...form, tasks: form.tasks.filter((_, x) => x !== i) })} className="text-muted-foreground hover:text-red-600 p-1"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => setForm({ ...form, tasks: [...form.tasks, ""] })} className="text-xs font-bold border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150">+ Add task</button>
          <button data-testid="onb-tpl-save" onClick={save} className="ml-auto bg-[#0A251D] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">{editing ? "Update" : "Create"}</button>
          {editing && <button onClick={() => { setEditing(null); setForm({ name: "", entity_id: "", is_default: false, tasks: [""] }); }} className="border px-3 py-1.5 text-xs">Cancel</button>}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Configured templates ({rows.length})</div>
        <div className="space-y-3">
          {rows.map((t) => (
            <div key={t.id} className="border p-4" data-testid={`onb-tpl-row-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-sm">{t.name}</span>
                  {t.is_default && <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#0A251D] text-white px-2 py-0.5 rounded font-bold">Default</span>}
                  <span className="ml-2 text-xs text-muted-foreground">{entities.find((e) => e.id === t.entity_id)?.name || "Global"} · {t.tasks?.length} tasks</span>
                </div>
                <div className="flex gap-2"><button onClick={() => edit(t)} className="text-muted-foreground hover:text-[#0A251D]"><Pencil size={13} /></button><button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={13} /></button></div>
              </div>
              <ol className="mt-2 text-sm space-y-0.5">
                {t.tasks?.map((task, i) => <li key={i} className="text-muted-foreground"><span className="text-[#0A251D] font-semibold">{i + 1}.</span> {task}</li>)}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Platform Settings ---------------- */
function SettingsPanel() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const load = () => api.get("/master/settings").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const parseVal = (val, type) => {
    if (type === "number") return +val;
    if (type === "boolean") return val === true || val === "true";
    if (type === "json") { try { return JSON.parse(val); } catch { return val; } }
    return val;
  };

  const save = async (s) => {
    if (!(s.key in edits)) return;
    try {
      await api.put(`/master/settings/${s.id}`, { value: parseVal(edits[s.key], s.value_type) });
      toast.success(`Saved '${s.label}'`);
      setEdits((e) => { const c = { ...e }; delete c[s.key]; return c; });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const grouped = rows.reduce((acc, s) => { const g = s.group || "general"; (acc[g] = acc[g] || []).push(s); return acc; }, {});

  return (
    <div className="border border-t-0 bg-white p-5" data-testid="settings-panel">
      {Object.entries(grouped).map(([g, items]) => (
        <div key={g} className="mb-6 last:mb-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">{g}</div>
          <div className="space-y-2">
            {items.map((s) => {
              const cur = s.key in edits ? edits[s.key] : s.value;
              return (
                <div key={s.id} className="flex items-start gap-3 border p-3" data-testid={`setting-row-${s.key}`}>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{s.label} <span className="text-[10px] text-muted-foreground font-normal">({s.key} · {s.value_type})</span></div>
                    <div className="text-xs text-muted-foreground">{s.description}</div>
                  </div>
                  {s.value_type === "boolean" ? (
                    <input type="checkbox" checked={!!cur} onChange={(e) => setEdits({ ...edits, [s.key]: e.target.checked })} className="mt-2 accent-[#0A251D]" data-testid={`setting-input-${s.key}`} />
                  ) : s.value_type === "json" ? (
                    <textarea value={typeof cur === "string" ? cur : JSON.stringify(cur, null, 2)} onChange={(e) => setEdits({ ...edits, [s.key]: e.target.value })} rows={3} className="border px-2 py-1 text-xs w-64" data-testid={`setting-input-${s.key}`} />
                  ) : (
                    <input type={s.value_type === "number" ? "number" : "text"} value={cur ?? ""} onChange={(e) => setEdits({ ...edits, [s.key]: e.target.value })} className="border px-2 py-1.5 text-sm w-40" data-testid={`setting-input-${s.key}`} />
                  )}
                  <button data-testid={`setting-save-${s.key}`} disabled={!(s.key in edits)} onClick={() => save(s)}
                    className="text-xs bg-[#0A251D] text-white px-3 py-1.5 font-bold hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-40 flex items-center gap-1">
                    <Save size={12} /> Save
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Users CRUD ---------------- */
function UsersPanel({ entities }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "recruiter", entity_id: "", password: "Welcome@123", active: true });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/master/users/all").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.role) return toast.error("Name, email and role required");
    try {
      const payload = { ...form, entity_id: form.entity_id || null };
      if (editing) {
        const { email, password, ...upd } = payload;
        if (password && password !== "Welcome@123") upd.password = password;
        await api.put(`/master/users/${editing}`, upd);
        toast.success("User updated");
      } else {
        await api.post("/master/users", payload);
        toast.success(`User created — temp password: ${payload.password}`);
      }
      setShowForm(false); setEditing(null);
      setForm({ name: "", email: "", role: "recruiter", entity_id: "", password: "Welcome@123", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (u) => { setForm({ name: u.name, email: u.email, role: u.role, entity_id: u.entity_id || "", password: "", active: u.active !== false }); setEditing(u.id); setShowForm(true); };
  const remove = async (id) => {
    if (!window.confirm("Delete this user? Demo accounts will be deactivated instead.")) return;
    try { const { data } = await api.delete(`/master/users/${id}`); toast.success(data.deactivated ? "User deactivated" : "User deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-t-0 bg-white p-5" data-testid="users-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Users ({users.length})</div>
        <button data-testid="user-new-button" onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", email: "", role: "recruiter", entity_id: "", password: "Welcome@123", active: true }); }}
          className="flex items-center gap-1.5 bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
          <Plus size={13} /> New user
        </button>
      </div>
      {showForm && (
        <div className="border p-4 mb-4 grid md:grid-cols-3 gap-3" data-testid="user-form">
          <input data-testid="user-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border px-3 py-2 text-sm" />
          <input data-testid="user-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border px-3 py-2 text-sm" disabled={!!editing} />
          <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border px-3 py-2 text-sm bg-white">
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <select data-testid="user-entity-select" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="border px-3 py-2 text-sm bg-white">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <input data-testid="user-password-input" placeholder={editing ? "New password (leave blank to keep)" : "Password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border px-3 py-2 text-sm" />
          <label className="text-xs flex items-center gap-2 px-3 py-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#0A251D]" /> Active</label>
          <div className="md:col-span-3 flex gap-2">
            <button data-testid="user-save-button" onClick={save} className="bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">{editing ? "Update" : "Create"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="border px-4 py-2 text-xs">Cancel</button>
          </div>
        </div>
      )}
      <DataTable
        testId="users-dt"
        rows={users}
        pageSize={10}
        searchKeys={["name", "email", "role"]}
        filters={[
          { key: "role", label: "Role", options: [...new Set(users.map((u) => u.role))].map((r) => ({ value: r, label: r.replace(/_/g, " ") })) },
        ]}
        columns={[
          { key: "name", label: "Name", sortable: true, render: (u) => <span className="font-semibold">{u.name}</span> },
          { key: "email", label: "Email", sortable: true, render: (u) => <span className="text-muted-foreground">{u.email}</span> },
          { key: "role", label: "Role", sortable: true, render: (u) => <span className="capitalize">{u.role?.replace(/_/g, " ")}</span> },
          { key: "entity_id", label: "Entity", render: (u) => entities.find((e) => e.id === u.entity_id)?.name || "—" },
          { key: "active", label: "Active", render: (u) => u.active === false ? <span className="text-red-600 text-xs font-bold">DISABLED</span> : "✓" },
          { key: "actions", label: "", render: (u) => (
            <div className="flex gap-2 justify-end">
              <button data-testid={`user-edit-${u.id}`} onClick={() => edit(u)} className="text-muted-foreground hover:text-[#0A251D]"><Pencil size={13} /></button>
              <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u.id)} className="text-muted-foreground hover:text-red-600"><Trash2 size={13} /></button>
            </div>
          )},
        ]}
      />
    </div>
  );
}

/* ---------------- Main page ---------------- */
export default function MasterData() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("entities");
  const [rows, setRows] = useState([]);
  const entities = useEntities();
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    if (SIMPLE_TABS.some((t) => t.key === tab)) {
      api.get(`/master/${tab}`).then((r) => setRows(r.data)).catch(() => {});
    }
  }, [tab]);

  useEffect(() => { load(); setForm({}); }, [load]);

  const tabDef = SIMPLE_TABS.find((t) => t.key === tab);

  const create = async () => {
    try {
      const payload = { ...form };
      if (tab === "departments" && !payload.entity_id) payload.entity_id = entities[0]?.id;
      if (tab === "salary-bands") { payload.min_lpa = +payload.min_lpa || 0; payload.max_lpa = +payload.max_lpa || 0; }
      if (tab === "stages") { payload.order = +payload.order || 100; }
      if (tab === "connectors") {
        payload.order = +payload.order || 100;
        payload.mode = payload.mode || "SIMULATED";
        payload.needs_key = payload.mode !== "LIVE";
        payload.supports_import = true;
      }
      await api.post(`/master/${tab}`, payload);
      toast.success("Created");
      setForm({}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/master/${tab}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const entName = (id) => entities.find((e) => e.id === id)?.name || "—";

  return (
    <div data-testid="master-data-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">DIY Admin</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Master Data</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2"><ShieldCheck size={13} /> Every knob of TalentOS AI is DB-driven. Nothing is hardcoded — you own the configuration.</p>
        </div>
      </div>

      <div className="flex gap-1 mt-5 border-b flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`master-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === t.key ? "border-[#0A251D] text-[#0A251D]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tabDef && (
        <>
          <div className="border border-t-0 bg-white p-5 flex flex-wrap items-end gap-3" data-testid="master-create-form">
            {tabDef.fields.map(([key, label, type]) => (
              <div key={key}>
                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">{label}</label>
                {type === "entity" ? (
                  <select value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 block border px-3 py-2 text-sm bg-white w-44">
                    <option value="">Select…</option>
                    {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                ) : (
                  <input data-testid={`master-input-${key}`} type={type === "number" ? "number" : "text"} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="mt-1 block border px-3 py-2 text-sm w-44" />
                )}
              </div>
            ))}
            <button data-testid="master-add-button" onClick={create}
              className="flex items-center gap-1.5 bg-[#0A251D] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
              <Plus size={13} /> Add
            </button>
          </div>

          <div className="border border-t-0 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-secondary/50 text-left">
                {tabDef.fields.map(([k, l]) => <th key={k} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{l}</th>)}
                <th className="px-4 py-3 w-24"></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0" data-testid={`master-row-${r.id}`}>
                    {tabDef.fields.map(([k, , type]) => (
                      <td key={k} className="px-4 py-3">
                        {type === "entity" ? entName(r[k]) : k === "color" && r[k] ? <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: r[k] }} /> {r[k]}</span> : String(r[k] ?? "—")}
                      </td>
                    ))}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {tab === "entities" && (
                        <button data-testid={`entity-reqs-link-${r.id}`} onClick={() => navigate(`/requisitions?entity=${r.id}`)}
                          className="text-xs font-bold text-[#E05A47] hover:underline mr-3">Requisitions →</button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={tabDef.fields.length + 1} className="px-4 py-6 text-center text-sm text-muted-foreground">No records yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "workflows" && <WorkflowsPanel />}
      {tab === "interview-templates" && <InterviewTemplatesPanel />}
      {tab === "onboarding-templates" && <OnboardingTemplatesPanel />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "users" && <UsersPanel entities={entities} />}
    </div>
  );
}
